import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Button,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    message,
    Tag,
    Space,
    Tabs,
    Popconfirm,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ThunderboltOutlined,
    EyeOutlined,
    HomeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
    loadWFHRequests,
    createWFHRequest,
    approveWFHRequest,
    rejectWFHRequest,
    loadWFHBalances,
    generateWFHCredits,
    loadWFHCreditLogs,
    loadEmployeesByOrganization,
} from 'store/actions/AuthActions';
import useIsMobile from 'hooks/useIsMobile';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const getStatusColor = (s) => {
    const map = { pending: 'orange', approved: 'green', rejected: 'red', cancelled: 'gray' };
    return map[s] || 'blue';
};

// Active tab, years and pagination live in the URL so a view can be shared as a link.
// Years have no schema default: the fallback is "now", and a link must keep its year.
const URL_FILTERS = {
    tab: { type: 'string', default: 'requests' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 15 },
    balYear: { type: 'number', key: 'bal_year' },
    logYear: { type: 'number', key: 'log_year' },
    logPage: { type: 'number', key: 'log_page', default: 1 },
    logPageSize: { type: 'number', key: 'log_page_size', default: 15 },
};

const WorkFromHome = () => {
    const isMobile = useIsMobile();
    const organization = useSelector((state) => state.auth.organization);
    const authUser = useSelector((state) => state.auth.user);
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);
    const activeTab = filters.tab;
    // Page sizes from the URL are clamped to what the API will return in one page
    const reqPageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const logPageSize = Math.min(filters.logPageSize, MAX_PAGE_SIZE);
    // Back to page 1 when the page size changes
    const pageSetter = (pageKey, sizeKey, size) => (p, ps) =>
        setFilters({ [pageKey]: ps !== size ? 1 : p, [sizeKey]: ps });

    // Requests
    const [requests, setRequests] = useState([]);
    const [reqLoading, setReqLoading] = useState(false);
    const [reqPagination, setReqPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [reqStats, setReqStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

    // Balances
    const [balances, setBalances] = useState([]);
    const [balLoading, setBalLoading] = useState(false);
    const balYear = filters.balYear || dayjs().year();

    // Credit Logs
    const [creditLogs, setCreditLogs] = useState([]);
    const [creditLogLoading, setCreditLogLoading] = useState(false);
    const [creditLogPagination, setCreditLogPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const creditLogYear = filters.logYear || dayjs().year();

    // Credit generation
    const [creditYear, setCreditYear] = useState(dayjs().year());
    const [creditMonth, setCreditMonth] = useState(dayjs().month() + 1);
    const [creditLoading, setCreditLoading] = useState(false);

    // Modal + employees
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState('');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [actionReason, setActionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // ─── Fetchers ─────────────────────────────────────────────────────
    const fetchRequests = useCallback(async (page = 1, pageSize = 15) => {
        setReqLoading(true);
        try {
            const res = await loadWFHRequests(page, pageSize);
            if (res?.data) {
                const results = res.data.results || res.data;
                const arr = Array.isArray(results) ? results : [];
                setRequests(arr);
                const total = res.data.count ?? arr.length;
                setReqPagination({ current: page, pageSize, total });
                setReqStats({
                    total,
                    pending: arr.filter(r => r.status === 'pending').length,
                    approved: arr.filter(r => r.status === 'approved').length,
                    rejected: arr.filter(r => r.status === 'rejected').length,
                });
            }
        } catch { /* already notified */ }
        setReqLoading(false);
    }, []);

    const fetchBalances = useCallback(async (year) => {
        setBalLoading(true);
        try {
            const res = await loadWFHBalances(1, 500, year || balYear);
            if (res?.data) {
                const results = res.data.results || res.data;
                setBalances(Array.isArray(results) ? results : []);
            }
        } catch { /* */ }
        setBalLoading(false);
    }, [balYear]);

    const fetchCreditLogs = useCallback(async (page = 1, pageSize = 15, year) => {
        setCreditLogLoading(true);
        try {
            const res = await loadWFHCreditLogs(page, pageSize, { year: year || creditLogYear });
            if (res?.data) {
                const results = res.data.results || res.data;
                setCreditLogs(Array.isArray(results) ? results : []);
                setCreditLogPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } catch { /* */ }
        setCreditLogLoading(false);
    }, [creditLogYear]);

    const fetchEmployees = useCallback(async () => {
        if (!organization) return;
        try {
            const res = await loadEmployeesByOrganization(organization, 1, null, 1000);
            if (res?.results) setEmployees(res.results);
        } catch { /* */ }
    }, [organization]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useEffect(() => {
        fetchRequests(filters.page, reqPageSize);
    }, [fetchRequests, filters.page, reqPageSize]);

    // Other tabs load lazily, when opened, from the URL state
    useEffect(() => {
        if (activeTab === 'balances') fetchBalances(balYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, balYear]);

    useEffect(() => {
        if (activeTab === 'creditLogs') fetchCreditLogs(filters.logPage, logPageSize, creditLogYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filters.logPage, logPageSize, creditLogYear]);

    // ─── Modal helpers ────────────────────────────────────────────────
    const openModal = (type, record = null) => {
        setModalType(type);
        setSelectedRecord(record);
        setActionReason('');
        if (type === 'createRequest') {
            form.resetFields();
            // Default employee to the current user's employee if linked
            const myEmpId = authUser?.employee?.id || authUser?.employee_id;
            if (myEmpId) form.setFieldsValue({ employee: myEmpId });
        }
        setModalVisible(true);
    };
    const closeModal = () => {
        setModalVisible(false);
        setModalType('');
        setSelectedRecord(null);
        form.resetFields();
    };

    const handleSubmit = async (values) => {
        setSaving(true);
        try {
            if (modalType === 'createRequest') {
                const [start, end] = values.wfh_dates || [];
                const payload = {
                    employee: values.employee,
                    start_date: start ? start.format('YYYY-MM-DD') : null,
                    end_date: end ? end.format('YYYY-MM-DD') : null,
                    reason: values.reason,
                };
                await createWFHRequest(payload);
                message.success('WFH request submitted');
                closeModal();
                fetchRequests(reqPagination.current, reqPagination.pageSize);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail
                || (err?.response?.data && typeof err.response.data === 'object'
                    ? Object.values(err.response.data).flat().join(', ')
                    : null)
                || err?.message
                || 'Failed to save';
            message.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (record) => {
        setActionLoading(true);
        try {
            await approveWFHRequest(record.id);
            message.success('Request approved');
            fetchRequests(reqPagination.current, reqPagination.pageSize);
            fetchBalances();
        } catch (err) {
            message.error(err?.response?.data?.error || 'Failed to approve');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (record) => {
        setActionLoading(true);
        try {
            await rejectWFHRequest(record.id, actionReason);
            message.success('Request rejected');
            closeModal();
            fetchRequests(reqPagination.current, reqPagination.pageSize);
        } catch (err) {
            message.error(err?.response?.data?.error || 'Failed to reject');
        } finally {
            setActionLoading(false);
        }
    };

    const handleGenerateCredits = async () => {
        setCreditLoading(true);
        try {
            const result = await generateWFHCredits(creditYear, creditMonth);
            message.success(
                `Credits generated: ${result.balances_credited || 0} balances updated, `
                + `${result.employees_processed || 0} employees processed`
            );
            fetchBalances(creditYear);
            fetchCreditLogs(1, creditLogPagination.pageSize, creditYear);
        } catch (err) {
            message.error(err?.response?.data?.error || 'Failed to generate credits');
        } finally {
            setCreditLoading(false);
        }
    };

    // ─── Columns ──────────────────────────────────────────────────────
    const reqColumns = [
        {
            title: 'Employee',
            dataIndex: 'employee_name',
            key: 'employee_name',
            render: (t, r) => t || r.employee || '-',
        },
        {
            title: 'Dates',
            key: 'dates',
            render: (_, r) =>
                r.start_date === r.end_date
                    ? dayjs(r.start_date).format('DD MMM YYYY')
                    : `${dayjs(r.start_date).format('DD MMM')} - ${dayjs(r.end_date).format('DD MMM YYYY')}`,
        },
        { title: 'Days', dataIndex: 'total_days', key: 'total_days', width: 80 },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (s) => <Tag color={getStatusColor(s)}>{(s || '').toUpperCase()}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 210,
            render: (_, r) => (
                <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => openModal('viewRequest', r)}>
                        View
                    </Button>
                    {r.status === 'pending' && (
                        <>
                            <Popconfirm
                                title="Approve this WFH request?"
                                onConfirm={() => handleApprove(r)}
                            >
                                <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                                    Approve
                                </Button>
                            </Popconfirm>
                            <Button
                                size="small"
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => openModal('rejectRequest', r)}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const balColumns = [
        { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
        { title: 'Year', dataIndex: 'year', key: 'year', width: 80 },
        { title: 'Allocated', dataIndex: 'allocated_days', key: 'allocated_days', width: 100 },
        { title: 'Used', dataIndex: 'used_days', key: 'used_days', width: 80 },
        {
            title: 'Available',
            dataIndex: 'available_days',
            key: 'available_days',
            width: 100,
            render: (v) => <strong>{v}</strong>,
        },
        { title: 'Pending', dataIndex: 'pending_days', key: 'pending_days', width: 90 },
        {
            title: 'Credited Through',
            dataIndex: 'credited_through_month',
            key: 'credited_through_month',
            width: 140,
            render: (m) => (m ? dayjs().month(m - 1).format('MMMM') : '-'),
        },
        {
            title: 'Last Credited At',
            dataIndex: 'last_credited_at',
            key: 'last_credited_at',
            render: (t) => (t ? dayjs(t).format('DD MMM YYYY HH:mm') : '-'),
        },
    ];

    const creditLogColumns = [
        { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
        { title: 'Year', dataIndex: 'year', key: 'year', width: 80 },
        {
            title: 'Month',
            dataIndex: 'month',
            key: 'month',
            width: 100,
            render: (m) => dayjs().month(m - 1).format('MMMM'),
        },
        { title: 'Days', dataIndex: 'days_credited', key: 'days_credited', width: 80 },
        { title: 'Before', dataIndex: 'balance_before', key: 'balance_before', width: 90 },
        { title: 'After', dataIndex: 'balance_after', key: 'balance_after', width: 90 },
        {
            title: 'Pro-rated',
            dataIndex: 'was_prorated',
            key: 'was_prorated',
            width: 100,
            render: (v) => (v ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>),
        },
        { title: 'Generated By', dataIndex: 'triggered_by_name', key: 'triggered_by_name' },
        {
            title: 'At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (t) => (t ? dayjs(t).format('DD MMM YYYY HH:mm') : '-'),
        },
    ];

    // ─── Tabs ─────────────────────────────────────────────────────────
    const tabItems = [
        {
            key: 'requests',
            label: 'Requests',
            children: (
                <div>
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic title="Total" value={reqStats.total} valueStyle={{ fontSize: 20 }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic title="Pending" value={reqStats.pending} valueStyle={{ fontSize: 20, color: '#fa8c16' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic title="Approved" value={reqStats.approved} valueStyle={{ fontSize: 20, color: '#52c41a' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic title="Rejected" value={reqStats.rejected} valueStyle={{ fontSize: 20, color: '#f5222d' }} />
                            </Card>
                        </Col>
                    </Row>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => openModal('createRequest')}
                            size={isMobile ? 'small' : 'middle'}
                        >
                            New WFH Request
                        </Button>
                    </div>
                    <Table
                        columns={reqColumns}
                        dataSource={requests}
                        loading={reqLoading}
                        rowKey="id"
                        pagination={{
                            ...reqPagination,
                            showSizeChanger: !isMobile,
                            simple: isMobile,
                            onChange: pageSetter('page', 'pageSize', reqPageSize),
                        }}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 900 }}
                    />
                </div>
            ),
        },
        {
            key: 'balances',
            label: 'Balances',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Space>
                            <span>Year:</span>
                            <DatePicker
                                picker="year"
                                value={dayjs().year(balYear)}
                                onChange={(v) => {
                                    const y = v ? v.year() : dayjs().year();
                                    setFilters({ balYear: y });
                                }}
                                style={{ width: 120 }}
                                allowClear={false}
                            />
                        </Space>
                        <Space wrap>
                            <span>Credit:</span>
                            <DatePicker
                                picker="year"
                                value={dayjs().year(creditYear)}
                                onChange={(v) => setCreditYear(v ? v.year() : dayjs().year())}
                                style={{ width: 110 }}
                                allowClear={false}
                            />
                            <Select
                                value={creditMonth}
                                onChange={setCreditMonth}
                                style={{ width: 140 }}
                            >
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <Option key={i + 1} value={i + 1}>
                                        {dayjs().month(i).format('MMMM')}
                                    </Option>
                                ))}
                            </Select>
                            <Popconfirm
                                title={`Generate WFH credits up to ${dayjs().month(creditMonth - 1).format('MMMM')} ${creditYear}?`}
                                description="This credits 2 days/month to every active employee, respecting joining date."
                                onConfirm={handleGenerateCredits}
                            >
                                <Button
                                    type="primary"
                                    icon={<ThunderboltOutlined />}
                                    loading={creditLoading}
                                >
                                    Generate Credits
                                </Button>
                            </Popconfirm>
                        </Space>
                    </div>
                    <Table
                        columns={balColumns}
                        dataSource={balances}
                        loading={balLoading}
                        rowKey="id"
                        pagination={false}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 1000 }}
                    />
                </div>
            ),
        },
        {
            key: 'creditLogs',
            label: 'Credit Logs',
            children: (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <Space>
                            <span>Year:</span>
                            <DatePicker
                                picker="year"
                                value={dayjs().year(creditLogYear)}
                                onChange={(v) => {
                                    const y = v ? v.year() : dayjs().year();
                                    setFilters({ logYear: y, logPage: 1 });
                                }}
                                style={{ width: 120 }}
                                allowClear={false}
                            />
                        </Space>
                    </div>
                    <Table
                        columns={creditLogColumns}
                        dataSource={creditLogs}
                        loading={creditLogLoading}
                        rowKey="id"
                        pagination={{
                            ...creditLogPagination,
                            showSizeChanger: !isMobile,
                            simple: isMobile,
                            onChange: pageSetter('logPage', 'logPageSize', logPageSize),
                        }}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 1000 }}
                    />
                </div>
            ),
        },
    ];

    // Fetch data on tab change
    const onTabChange = (key) => setFilters({ tab: key }, { replace: false });

    // ─── Modal content ────────────────────────────────────────────────
    const renderModalContent = () => {
        if (modalType === 'createRequest') {
            return (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        label="Employee"
                        name="employee"
                        rules={[{ required: true, message: 'Select an employee' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select employee"
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {employees.map((e) => {
                                const name = `${e.user?.first_name || ''} ${e.user?.last_name || ''}`.trim()
                                    || e.user?.email
                                    || 'Unnamed';
                                const label = e.employee_id ? `${name} (${e.employee_id})` : name;
                                return (
                                    <Option key={e.id} value={e.id}>
                                        {label}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="WFH Dates"
                        name="wfh_dates"
                        rules={[{ required: true, message: 'Select dates' }]}
                    >
                        <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Reason"
                        name="reason"
                        rules={[{ required: true, message: 'Enter reason' }]}
                    >
                        <TextArea rows={3} placeholder="Reason for WFH" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                Submit
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            );
        }

        if (modalType === 'rejectRequest') {
            return (
                <div>
                    <p>Reject WFH request for <strong>{selectedRecord?.employee_name}</strong>?</p>
                    <TextArea
                        rows={3}
                        placeholder="Rejection reason (optional)"
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                    />
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button
                                danger
                                type="primary"
                                loading={actionLoading}
                                onClick={() => handleReject(selectedRecord)}
                            >
                                Reject
                            </Button>
                        </Space>
                    </div>
                </div>
            );
        }

        if (modalType === 'viewRequest' && selectedRecord) {
            const r = selectedRecord;
            return (
                <div>
                    <p><strong>Employee:</strong> {r.employee_name}</p>
                    <p><strong>Dates:</strong> {dayjs(r.start_date).format('DD MMM YYYY')} – {dayjs(r.end_date).format('DD MMM YYYY')} ({r.total_days} day(s))</p>
                    <p><strong>Status:</strong> <Tag color={getStatusColor(r.status)}>{(r.status || '').toUpperCase()}</Tag></p>
                    <p><strong>Reason:</strong> {r.reason}</p>
                    {r.rejection_reason && <p><strong>Rejection reason:</strong> {r.rejection_reason}</p>}
                    <p><strong>Applied by:</strong> {r.applied_by_name || '-'} on {dayjs(r.created_at).format('DD MMM YYYY HH:mm')}</p>
                    {r.approved_by && (
                        <p>
                            <strong>{r.status === 'approved' ? 'Approved' : 'Actioned'} by:</strong>{' '}
                            {r.approved_by_name || '-'} on {r.approved_at ? dayjs(r.approved_at).format('DD MMM YYYY HH:mm') : '-'}
                        </p>
                    )}
                </div>
            );
        }

        return null;
    };

    const modalTitle = {
        createRequest: 'New WFH Request',
        viewRequest: 'WFH Request Details',
        rejectRequest: 'Reject WFH Request',
    }[modalType] || '';

    return (
        <div style={{ padding: isMobile ? 12 : 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <HomeOutlined style={{ fontSize: 20 }} />
                <h2 style={{ margin: 0 }}>Work From Home</h2>
            </div>
            <Tabs activeKey={activeTab} onChange={onTabChange} items={tabItems} />

            <Modal
                title={modalTitle}
                open={modalVisible}
                onCancel={closeModal}
                footer={null}
                width={isMobile ? '95%' : 600}
                destroyOnClose
            >
                {renderModalContent()}
            </Modal>
        </div>
    );
};

export default WorkFromHome;
